import React, { useState } from 'react';
import { X, ShieldCheck, AlertCircle, FileText, UserSquare, Loader2 } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';

import { API_BASE } from '../config';

// const API_BASE = "http://127.0.0.1:8000";


export default function CandidateVerificationModal({ isOpen, onClose, resumeId, candidateName, onVerifyComplete, prefillData }) {
    const [refImage, setRefImage] = useState(null);
    const [authDoc, setAuthDoc] = useState(null);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    // Track if we are keeping existing files
    const [keepRef, setKeepRef] = useState(false);
    const [keepDoc, setKeepDoc] = useState(false);

    // Reset state when opening
    React.useEffect(() => {
        if (isOpen) {
            setResult(null);

            if (prefillData) {
                // If re-verifying reference, keep existing auth doc
                if (prefillData.type === 'reference') {
                    setRefImage(null);
                    setAuthDoc({ name: "Existing ID Document", url: prefillData.docUrl });
                    setKeepDoc(true);
                    setKeepRef(false);
                }
                // If re-verifying identity, keep existing reference photo
                else if (prefillData.type === 'identity') {
                    setAuthDoc(null);
                    setRefImage({ name: "Existing Photo", url: prefillData.refUrl });
                    setKeepRef(true);
                    setKeepDoc(false);
                }
            } else {
                setRefImage(null);
                setAuthDoc(null);
                setKeepRef(false);
                setKeepDoc(false);
            }
        }
    }, [isOpen, resumeId, prefillData]);

    if (!isOpen) return null;

    const handleVerify = async () => {
        if (!refImage || !authDoc) {
            toast.error("Required documents missing.");
            return;
        }

        setLoading(true);
        const formData = new FormData();

        // Only append if it's a new File object (not the "Existing" placeholder object)
        if (refImage instanceof File) {
            formData.append('reference_image', refImage);
        }
        if (authDoc instanceof File) {
            formData.append('auth_doc', authDoc);
        }

        try {
            const token = localStorage.getItem('token');
            const res = await axios.post(`${API_BASE}/resumes/${resumeId}/verify`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Authorization': `Bearer ${token}`
                }
            });

            setResult(res.data);
            if (res.data.verified) {
                toast.success("Candidate Verified Successfully!");
                if (onVerifyComplete) onVerifyComplete();
            } else {
                toast.error("Verification Failed: " + res.data.message);
            }
        } catch (err) {
            console.error(err);
            if (err.response && err.response.status === 401) {
                toast.error("Session expired. Please log in again.");
                // Optional: Trigger logout callback if available, but for now just notify.
                onClose();
            } else {
                toast.error("Verification error. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-slate-100 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/50">
                    <div>
                        <h2 className="text-xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
                            <ShieldCheck className="w-6 h-6 text-brand-600" />
                            Verify Candidate Identity
                        </h2>
                        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                            Verify <strong>{candidateName}</strong> against an official ID document.
                        </p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-full transition-colors">
                        <X className="w-5 h-5 text-slate-500" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto space-y-6">
                    {!result ? (
                        <>
                            <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-800 rounded-xl p-4 flex gap-3 text-sm text-blue-800 dark:text-blue-200">
                                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                                <p>
                                    To verify this candidate, please upload a trusted <strong>Reference Photo</strong> (e.g. LinkedIn profile picture) and their <strong>Aadhaar Card</strong>.
                                </p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Reference Image Upload */}
                                <div className="space-y-3">
                                    <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300">
                                        1. Reference Photo (Face)
                                    </label>
                                    <div className={`border-2 border-dashed ${keepRef ? 'border-brand-500' : 'border-slate-300 dark:border-slate-600'} rounded-xl p-6 text-center hover:border-brand-400 transition-colors bg-slate-50 dark:bg-slate-800/50`}>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) => {
                                                setRefImage(e.target.files[0]);
                                                setKeepRef(false);
                                            }}
                                            className="hidden"
                                            id="ref-upload"
                                        />
                                        <label htmlFor="ref-upload" className="cursor-pointer block">
                                            {refImage ? (
                                                <div className="relative group">
                                                    <img
                                                        src={refImage instanceof File ? URL.createObjectURL(refImage) : refImage.url}
                                                        alt="Ref"
                                                        className="w-32 h-32 object-cover rounded-full mx-auto shadow-md"
                                                    />
                                                    <div className="absolute inset-0 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/30 rounded-full">
                                                        <span className="text-white text-xs font-medium">Change</span>
                                                    </div>
                                                    {keepRef && (
                                                        <div className="mt-2 text-[10px] font-bold text-brand-600 bg-brand-50 px-2 py-0.5 rounded-full inline-block">
                                                            KEEPING ORIGINAL
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="space-y-2 py-4">
                                                    <div className="w-12 h-12 bg-white dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto shadow-sm">
                                                        <UserSquare className="w-6 h-6 text-slate-400" />
                                                    </div>
                                                    <p className="text-sm font-medium text-brand-600">Upload Photo</p>
                                                    <p className="text-xs text-slate-400">LinkedIn or Interview Shot</p>
                                                </div>
                                            )}
                                        </label>
                                    </div>
                                </div>

                                {/* Auth Doc Upload */}
                                <div className="space-y-3">
                                    <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300">
                                        2. Identity Proof (Aadhaar)
                                    </label>
                                    <div className={`border-2 border-dashed ${keepDoc ? 'border-brand-500' : 'border-slate-300 dark:border-slate-600'} rounded-xl p-6 text-center hover:border-brand-400 transition-colors bg-slate-50 dark:bg-slate-800/50`}>
                                        <input
                                            type="file"
                                            accept="image/*,.pdf,application/pdf"
                                            onChange={(e) => {
                                                setAuthDoc(e.target.files[0]);
                                                setKeepDoc(false);
                                            }}
                                            className="hidden"
                                            id="auth-upload"
                                        />
                                        <label htmlFor="auth-upload" className="cursor-pointer block">
                                            {authDoc ? (
                                                <div className="relative group">
                                                    <img
                                                        src={authDoc instanceof File ? URL.createObjectURL(authDoc) : authDoc.url}
                                                        alt="Doc"
                                                        className="w-full h-32 object-cover rounded-lg shadow-md"
                                                    />
                                                    <div className="absolute inset-0 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/30 rounded-lg text-white">
                                                        <span className="text-xs font-medium">Change</span>
                                                    </div>
                                                    {keepDoc ? (
                                                        <div className="mt-2 text-[10px] font-bold text-brand-600 bg-brand-50 px-2 py-0.5 rounded-full inline-block">
                                                            KEEPING ORIGINAL
                                                        </div>
                                                    ) : (
                                                        <p className="text-xs text-slate-500 mt-2 truncate">{authDoc.name}</p>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="space-y-2 py-4">
                                                    <div className="w-12 h-12 bg-white dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto shadow-sm">
                                                        <FileText className="w-6 h-6 text-slate-400" />
                                                    </div>
                                                    <p className="text-sm font-medium text-brand-600">Upload Aadhaar</p>
                                                    <p className="text-xs text-slate-400">Image or PDF</p>
                                                </div>
                                            )}
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className={`text-center py-8 rounded-xl ${result.verified ? 'bg-green-50 dark:bg-green-900/10' : 'bg-red-50 dark:bg-red-900/10'}`}>
                            <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-4 ${result.verified ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
                                {result.verified ? <ShieldCheck className="w-10 h-10" /> : <AlertCircle className="w-10 h-10" />}
                            </div>
                            <h3 className={`text-xl font-bold mb-2 ${result.verified ? 'text-green-800 dark:text-green-200' : 'text-red-800 dark:text-red-200'}`}>
                                {result.verified ? "Verification Successful" : "Verification Failed"}
                            </h3>
                            <p className="text-slate-600 dark:text-slate-400 max-w-sm mx-auto">
                                {result.message}
                            </p>
                            {result.verified && (
                                <p className="text-xs text-green-600 mt-4 font-mono">
                                    Match Score: {result.score.toFixed(4)} (Lower is better)
                                </p>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-slate-100 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/50 flex justify-end gap-3">
                    {result ? (
                        <>
                            {!result.verified && (
                                <button
                                    onClick={() => setResult(null)}
                                    className="px-6 py-2.5 text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white font-medium transition-colors"
                                >
                                    Try Again
                                </button>
                            )}
                            <button
                                onClick={onClose}
                                className="px-6 py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-medium transition-colors"
                            >
                                Close
                            </button>
                        </>
                    ) : (
                        <>
                            <button
                                onClick={onClose}
                                className="px-6 py-2.5 text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white font-medium transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleVerify}
                                disabled={loading || !refImage || !authDoc}
                                className="px-6 py-2.5 bg-brand-600 hover:bg-brand-700 text-white rounded-xl font-medium shadow-lg shadow-brand-500/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
                            >
                                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                                {loading ? "Verifying..." : "Verify Candidate"}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
